<template>
  <q-page>
    <div class="justify-center row">
      <div v-if="!$apollo.loading" class="col-10 q-pa-md">
        <h2 class="q-mt-sm q-mb-sm text-center">Brand Order {{order.orderNumber}}</h2>
        <div class="text-h4 text-center">({{order.name}})</div>
        <h3>Details</h3>
        <div class="col q-gutter-md">
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Company:</div>
            <div class="col-auto q-pl-md">
              <router-link :to="`/admin/accounts/${order.customerOrder.account.id}`">{{order.customerOrder.account.companyName}}</router-link>
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Group Order:</div>
            <div class="col-auto q-pl-md">
              <router-link :to="`/admin/group-orders/${order.groupOrder.id}`">{{order.groupOrder.name}} ({{order.groupOrder.isOpen ? 'Open' : 'Closed'}})</router-link>
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Order To:</div>
            <div class="col-auto q-pl-md">
              {{ findFieldInFilter(order, 'manufacturer') }}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Order Brand:</div>
            <div class="col-auto q-pl-md">
              {{ findFieldInFilter(order, 'brand') }}
            </div>
          </div>

          <m-edit-item field="generated" type="datetime" graphqlType="BrandOrder" :id="id" updateFunction="" />
          <m-edit-item field="verified" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="submitted" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="verifiedAtManufacturer" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="manufactured" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="shipped" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="arrived" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="checked" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="packed" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="dispatched" type="datetime" graphqlType="BrandOrder" :id="id" />
          <m-edit-item field="clientReceived" type="datetime" graphqlType="BrandOrder" :id="id" />
        </div>
        <h3 class="q-mb-none">Notes</h3>
        <m-edit-item label-type="none" autogrow field="notes" type="input" graphqlType="BrandOrder" :id="id"/>
        <h3>Brand Order</h3>
        <q-table
          :data="order.items"
          :columns="orderColumns"
          :pagination="{rowsPerPage: 0}"
          binary-state-sort
          row-key="name" >
          <template v-slot:body-cell-img="props">
            <q-td>
              <q-img dense :src="props.value + '.png.thumb'" style="width: 25px; height: 25px;" contain>
                <q-tooltip anchor="center right" self="center left" :offset="[10, 10]"
                           transition-show="scale"
                           transition-hide="scale"
                           content-style="background: white; border: solid 1px black">
                  <q-img dense :src="props.value" style="width: 25vw; height: 25vw;" contain/>
                </q-tooltip>
              </q-img>
            </q-td>
          </template>
            <template v-slot:body-cell-colour="props">
              <q-td :props="props">
                <div :style="{border: '1px silver solid', backgroundColor: props.row.sku.options.colour.hex, width: '10px',height: '10px',marginRight:'5px'}" class="inline-block"></div>
                {{props.row.sku.options.colour.group + ' (' + props.row.sku.options.colour.name + ')'}}
              </q-td>
            </template>
          <template v-slot:body-cell-reference="props">
            <q-td :props="props">
              <router-link :to="`/admin/products/${((props.row.sku||{}).product||{}).id}`">{{props.value}}</router-link>
            </q-td>
          </template>
        </q-table>

        <div class="text-h4 q-pt-md text-right">
          Customer Total Cost: {{ formatCurrency(order.totalCost) }}
        </div>
        <div class="text-h5 text-right text-grey">
          Includes GST: {{ formatCurrency(order.totalCost*(1-1/1.1)) }}
        </div>

        <h3 class="q-mt-none q-mb-md">Break Downs</h3>
        <div class="row">
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Brand"
              :items="order.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Type"
              :items="order.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Material"
              :items="order.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Colour"
              :items="order.items"
              :group-field-callback="row => (((row.sku||{}).options||{}).colour||{}).name">
              <template v-slot:group="props">
                <div :style="{border:'1px solid silver', backgroundColor: (((props.row.item.sku||{}).options||{}).colour||{}).hex, width: '10px',height: '10px'}" class="inline-block"></div>
                {{props.row.group}}
              </template>
            </summary-table>
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Screws You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="order.items"
            :group-field-callback="row => getProductAttributeFromRow('screws', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('screws', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>          
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Bolts You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="order.items"
            :group-field-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style>
</style>

<script>
import gql from 'graphql-tag'
import MEditItem from '../../components/ui/admin/MEditItem'
import { colourToString, formatCurrency } from '../../util/string'
import SummaryTable from '../../components/summaryTable'
import getNestedFieldInObject from '@/util/getNestedFieldInObject'

export default {
  name: 'PageAdminBrandOrder',
  components: { SummaryTable, MEditItem },
  apollo: {
    order () {
      return {
        query: gql`
        query brandOrder ($id: ID!) {
          brandOrder (id: $id) {
            id
            orderNumber
            filter

            groupOrder {
              id
              name
              isOpen
            }

            name
            notes
            createdAt
            totalCost

            generated
            verified
            submitted
            verifiedAtManufacturer
            manufactured
            shipped
            arrived
            checked
            packed
            dispatched
            clientReceived

            customerOrder {
              id
              account {
                id
                companyName
              }
            }

            items {
              id
#              skuId
              sku {
                  id
                  options
                  price
                  stock
                  product {
                      id
                      name
                      reference
                      attributes
                      images {
                        url
                      }
                      urlSlug
                  }
              }
              qty
              notes
              itemCost
              totalCost
            }
          }
        }
      `,
        variables: {
          id: this.id
        },
        update: result => result.brandOrder,
        fetchPolicy: 'network-only'
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    }
  },
  methods: {
    getNestedFieldInObject: getNestedFieldInObject,
    getProductAttributeFromRow (attributeName, row) {
      return this.getNestedFieldInObject(`sku.product.attributes.${attributeName}`, row) || []
    },   
    formatCurrency: formatCurrency,
    findFieldInFilter (row, field) {
      // example of field is 'manufacturer'
      return (row?.filter) ? row.filter.split('\n').find(r => r.startsWith(field)).replace(`${field}: `, '') : undefined;
    }
  },
  data () {
    return {
      order: {},
      orderColumns: [
        { name: 'img', field: row => row.sku?.product?.images?.[0]?.url },
        { name: 'reference', label: 'Reference', field: row => row.sku?.product?.reference, sortable: true, align: 'left' },
        { name: 'brand', label: 'Brand', field: row => row.sku?.product?.attributes?.brand, sortable: true, align: 'left' },
        { name: 'range', label: 'Range', field: row => row.sku?.product?.attributes?.range, sortable: true, align: 'left' },
        { name: 'name', label: 'Name', field: row => row.sku?.product?.name || 'Not Found', sortable: true, align: 'left' },
        // { name: 'size', label: 'Size', field: row => row.sku?.product?.attributes?.size, sortable: true },
        // { name: 'grip', label: 'Grip', field: row => row.sku?.product?.attributes?.grip, sortable: true },
        // { name: 'colour', label: 'Colour', field: row => colourToString(row.sku?.options?.colour), sortable: true, align: 'left' },
        { name: 'colour', label: 'Colour', field: row => colourToString(row.sku?.options?.colour), sortable: true, align: 'left' },
        { name: 'holds', label: 'Holds', field: row => Number(row.sku?.product?.attributes?.holds), sortable: true, align: 'right' },
        { name: 'price', label: 'Customer Price', field: row => Number(row.sku?.price), sortable: true, format: formatCurrency },
        { name: 'qty', label: 'Qty', field: row => Number(row.qty), sortable: true },
        { name: 'total', label: 'Customer Total', field: row => Number(row.totalCost), sortable: true, format: formatCurrency }
      ]
    }
  }
}
</script>
